import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type SyncPhase = 'idle' | 'processing' | 'done';

export interface SyncStatusSnapshot {
    phase: SyncPhase;
    /** Itens da fila prontos para envio (não em backoff / failed adiado). */
    pendingCount: number;
    /** Itens processados com sucesso no ciclo atual. */
    processedCount: number;
    /** Mensagem curta para o banner (vazia = não mostrar). */
    message: string;
    /** Overlay bloqueante só quando há trabalho real em andamento. */
    blocking: boolean;
}

const IDLE: SyncStatusSnapshot = {
    phase: 'idle',
    pendingCount: 0,
    processedCount: 0,
    message: '',
    blocking: false
};

/**
 * Controle único do estado da fila offline → API.
 * A UI (banner no layout) escuta `status$`; o SynchronizationService só reporta.
 * Snackbars rotineiros "Processando… / processada… Fechar" ficam fora daqui.
 */
@Injectable({ providedIn: 'root' })
export class SyncStatusService {
    private readonly _status$ = new BehaviorSubject<SyncStatusSnapshot>(IDLE);
    readonly status$ = this._status$.asObservable();

    get snapshot(): SyncStatusSnapshot {
        return this._status$.value;
    }

    /** Chamado antes de varrer a fila — sem UI se pending for 0. */
    beginScan(): void {
        // não altera UI; evita flash de overlay em varreduras vazias
    }

    /** Há itens a enviar agora. */
    startProcessing(pendingCount: number): void {
        if (pendingCount <= 0) {
            this.reset();
            return;
        }
        this._status$.next({
            phase: 'processing',
            pendingCount,
            processedCount: 0,
            message: pendingCount === 1
                ? 'Enviando 1 pend\u00EAncia...'
                : `Enviando ${pendingCount} pend\u00EAncias...`,
            blocking: true
        });
    }

    markItemDone(success: boolean): void {
        const cur = this._status$.value;
        if (cur.phase !== 'processing') {
            return;
        }
        const processedCount = cur.processedCount + (success ? 1 : 0);
        const remaining = Math.max(0, cur.pendingCount - 1);
        this._status$.next({
            ...cur,
            pendingCount: remaining,
            processedCount,
            message: remaining > 0
                ? `Enviando pend\u00EAncias (${remaining} restantes)...`
                : cur.message
        });
    }

    /**
     * Fim do ciclo. Banner "concluido" so se algo foi enviado de verdade;
     * varredura vazia ou so retries adiados -> some sem mensagem.
     */
    finish(processedCount?: number): void {
        const cur = this._status$.value;
        const done = processedCount ?? cur.processedCount;
        if (done <= 0) {
            this.reset();
            return;
        }
        this._status$.next({
            phase: 'done',
            pendingCount: 0,
            processedCount: done,
            message: done === 1
                ? '1 pend\u00EAncia enviada'
                : `${done} pend\u00EAncias enviadas`,
            blocking: false
        });
        setTimeout(() => {
            if (this._status$.value.phase === 'done') {
                this.reset();
            }
        }, 2500);
    }

    reset(): void {
        this._status$.next(IDLE);
    }
}
